import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { colors, tilePalette } from "@/theme";
import { ProductCard } from "@/components/ProductCard";
import { SectionTitle, ProductGridSkeleton } from "@/components/common";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1609081144289-eacc3108cd03?q=80&w=1600&auto=format&fit=crop";

function ProductRail({ eyebrow, title, products, isMobile }) {
  if (!products || products.length === 0) return null;
  return (
    <section className="container-max" style={{ padding: isMobile ? "32px 16px 0" : "48px 24px 0" }}>
      <SectionTitle eyebrow={eyebrow} title={title} />
      <div className="no-scrollbar" style={{ display: "grid", gridAutoFlow: "column", gridAutoColumns: isMobile ? "46%" : "23%", gap: isMobile ? 14 : 22, overflowX: "auto", paddingBottom: 6 }}>
        {products.map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
    </section>
  );
}

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [editorials, setEditorials] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [recent, setRecent] = useState([]);
  const [becauseLiked, setBecauseLiked] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const [c, e, f] = await Promise.all([
          api("/categories"),
          api("/editorials"),
          api("/products?featured=true"),
        ]);
        setCategories(c);
        setEditorials(e);
        setFeatured(f);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!user) { setRecommended([]); setRecent([]); return; }
    api("/recommendations?limit=8").then(setRecommended).catch(() => {});
    api("/recently-viewed?limit=8").then(setRecent).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user || recent.length === 0 || categories.length === 0) { setBecauseLiked(null); return; }
    const cat = recent[0].category;
    const catName = categories.find((c) => c.id === cat)?.name || cat;
    const viewedIds = new Set(recent.map((r) => r.id));
    api(`/products?category=${cat}`).then((ps) => {
      const items = ps.filter((p) => !viewedIds.has(p.id)).slice(0, 8);
      setBecauseLiked(items.length ? { name: catName, products: items } : null);
    }).catch(() => setBecauseLiked(null));
  }, [user, recent, categories]);


  return (
    <div className="fade-up">
      {/* Hero */}
      <section style={{ position: "relative", height: isMobile ? 460 : 560, overflow: "hidden" }}>
        <img src={HERO} alt="Aesthetic home interior" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(44,41,37,0.05) 0%, rgba(44,41,37,0.55) 100%)" }} />
        <div className="container-max" style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: isMobile ? "0 24px 48px" : "0 24px 72px" }}>
          <div className="eyebrow" style={{ color: "rgba(252,251,248,0.85)", marginBottom: 12 }}>The Quiet Home Edit</div>
          <h1 className="font-display" style={{ color: "#FCFBF8", margin: 0, fontSize: isMobile ? 40 : 60, lineHeight: 1.05, fontWeight: 700, maxWidth: 620 }}>
            Set a table worth savouring
          </h1>
          <p className="font-body" style={{ color: "rgba(252,251,248,0.9)", marginTop: 16, fontSize: isMobile ? 15 : 17, maxWidth: 480 }}>
            Handcrafted crockery, decor & home essentials in soft, muted tones.
          </p>
          <button data-testid="hero-cta" className="btn-primary" style={{ marginTop: 26, alignSelf: "flex-start", background: colors.surface, color: colors.onSurface }} onClick={() => navigate("/category/dining")}>
            Enter the Nest <ArrowRight size={15} style={{ marginLeft: 8, verticalAlign: "middle" }} />
          </button>
        </div>
      </section>

      {/* Category tiles */}
      <section className="container-max" style={{ padding: isMobile ? "36px 16px 8px" : "56px 24px 8px" }}>
        <div className="eyebrow" style={{ marginBottom: 18 }}>Shop by Category</div>
        <div className="no-scrollbar" style={{ display: "flex", gap: isMobile ? 18 : 30, overflowX: "auto", paddingBottom: 8 }}>
          {categories.map((cat, i) => (
            <Link key={cat.id} to={`/category/${cat.id}`} data-testid={`category-tile-${cat.id}`} style={{ textDecoration: "none", textAlign: "center", flex: "0 0 auto" }}>
              <div
                className="card-hover"
                style={{
                  width: isMobile ? 84 : 116, height: isMobile ? 84 : 116, borderRadius: 999, overflow: "hidden",
                  background: tilePalette[i % tilePalette.length], display: "flex", alignItems: "center", justifyContent: "center",
                  border: `1px solid ${colors.border}`,
                }}
              >
                <img src={cat.image} alt={cat.name} className="card-img" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              <div className="font-body" style={{ marginTop: 10, fontSize: 12.5, fontWeight: 500, color: colors.onSurface, maxWidth: isMobile ? 84 : 116 }}>{cat.name}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Personalized rails (logged-in) */}
      {becauseLiked && <ProductRail eyebrow="Inspired by your browsing" title={`Because you liked ${becauseLiked.name}`} products={becauseLiked.products} isMobile={isMobile} />}
      <ProductRail eyebrow="Just for You" title="Recommended for You" products={recommended} isMobile={isMobile} />
      <ProductRail eyebrow="Pick Up Where You Left Off" title="Recently Viewed" products={recent} isMobile={isMobile} />

      {/* Editorial sections */}
      {editorials.map((ed) => (
        <section key={ed.id} className="container-max" style={{ padding: isMobile ? "32px 16px 0" : "48px 24px 0" }}>
          <SectionTitle title={ed.title} subtitle={ed.subtitle} />
          <div className="no-scrollbar" style={{ display: "grid", gridAutoFlow: "column", gridAutoColumns: isMobile ? "44%" : "23%", gap: 16, overflowX: "auto", paddingBottom: 6 }}>
            {(ed.tiles || []).map((tile, ti) => {
              const params = new URLSearchParams(tile.filter || {}).toString();
              return (
                <Link key={ti} to={`/search?${params}&label=${encodeURIComponent(tile.label)}`} data-testid={`editorial-tile-${ed.id}-${ti}`} className="card-hover" style={{ textDecoration: "none" }}>
                  <div style={{ aspectRatio: "1 / 1", overflow: "hidden", border: `1px solid ${colors.border}` }}>
                    <img src={tile.image} alt={tile.label} className="card-img" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                  <div className="font-body" style={{ marginTop: 10, fontSize: 13.5, fontWeight: 500, color: colors.onSurface }}>{tile.label}</div>
                </Link>
              );
            })}
          </div>
        </section>
      ))}

      {/* Featured products */}
      <section className="container-max" style={{ padding: isMobile ? "36px 16px 24px" : "56px 24px 24px" }}>
        <SectionTitle eyebrow="Curated" title="Featured Pieces" />
        {loading ? <ProductGridSkeleton /> : (
          <div className="product-grid">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}
